import { ReceiveShipmentsService } from '@/api';
import { DeliveryRecapResponse } from '@/api/receive';
import { AppRoutes } from '@/app/routers';
import { ReactComponent as DeliveryIcon } from '@/assets/icons/delivery.svg';
import { CTAContainer } from '@/components/layout/CTAContainer';
import { ModalProductDetails } from '@/components/layout/ModalProductDetails';
import { UIBox } from '@/components/ui/Box';
import { Typography } from '@/components/ui/Typography';
import { UIWarning } from '@/components/ui/Warning';
import { useSelector } from '@/hooks/useSelector';
import {
  StyledFirstRow,
  StyledInfoWrapper,
  StyledProductsWrapper,
  StyledSecondRow,
} from '@/pages/Receiving/PageReceivingRecap/style';
import { colors } from '@/theme';
import { Product } from '@/types/enum';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { FC, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import ProductList from './ProductList';
import { ProductTable } from './ProductTable';

const PageReceivingRecap: FC = memo((): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation();
  const { store } = useSelector(state => state.currentStore);
  const { deliveryNumber } = useParams<{ deliveryNumber?: string }>();
  const [view, setView] = useState<string>('list');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [openProductModal, setProductModalVisibility] = useState(false);

  const [recap, setRecap] = useState<DeliveryRecapResponse | undefined>(
    undefined
  );

  useAsync(async () => {
    const recaps =
      await ReceiveShipmentsService.receiveshipGetDeliveryRecapInfo({
        requestBody: {
          deliveryNrList: [deliveryNumber || '0863568659'],
          storeCode: store?.storeCode!,
        },
      });
    const recap: DeliveryRecapResponse | undefined = recaps?.find(
      el => el.deliveryNr === deliveryNumber
    );

    const mockRecap: DeliveryRecapResponse = {
      deliveryNr: deliveryNumber || '0863568659',
      status: 'Confirmed',
      confirmationDate: '2025-03-18T00:00:00',
      eventId: '1378d91d-2c0a-11f0-9c58-5e981b6614c4',
      webUser: 'maria.rossi',
      items: [
        {
          quantity: 0,
          qtySold: 0,
          qtyFound: 1,
          qtyTotalFound: 1,
          adj: 1,
          movementType: '552',
          brandCode: '0RB',
          brandDescription: 'Coach',
          colorCode: '55488H',
          commodity: 'Sunglasses',
          imageUrl:
            '/cdn-record-files-pi/de2bc830-19f3-46d1-bc3c-abd800cbf9b9/cc3b839f-4791-49ac-a2fe-abd800cbfa44/0HC8132__55488H.jpg',
          modelCode: '0HC8132',
          sizeCode: '57',
          styleName: 'L109',
          upcCode: '725125358262',
        },
        {
          quantity: 0,
          qtySold: 0,
          qtyFound: 1,
          qtyTotalFound: 1,
          adj: 4,
          movementType: '551',
          brandCode: '0RB',
          brandDescription: 'Coach',
          colorCode: '55488H',
          commodity: 'Sunglasses',
          imageUrl:
            '/cdn-record-files-pi/de2bc830-19f3-46d1-bc3c-abd800cbf9b9/cc3b839f-4791-49ac-a2fe-abd800cbfa44/0HC8132__55488H.jpg',
          modelCode: '0HC8132',
          sizeCode: '57',
          styleName: 'L109',
          upcCode: '725125358262',
        },
        {
          quantity: 0,
          qtySold: 0,
          qtyFound: 1,
          qtyTotalFound: 1,
          adj: 1,
          movementType: '552',
          brandCode: '0RB',
          brandDescription: 'Coach',
          colorCode: '55488H',
          commodity: 'Sunglasses',
          imageUrl:
            '/cdn-record-files-pi/de2bc830-19f3-46d1-bc3c-abd800cbf9b9/cc3b839f-4791-49ac-a2fe-abd800cbfa44/0HC8132__55488H.jpg',
          modelCode: '0HC8132',
          sizeCode: '57',
          styleName: 'L109',
          upcCode: '725125358262',
        },
        {
          quantity: 0,
          qtySold: 0,
          qtyFound: 1,
          qtyTotalFound: 1,
          adj: 1,
          movementType: '552',
          brandCode: '0RB',
          brandDescription: 'Coach',
          colorCode: '55488H',
          commodity: 'Sunglasses',
          imageUrl:
            '/cdn-record-files-pi/de2bc830-19f3-46d1-bc3c-abd800cbf9b9/cc3b839f-4791-49ac-a2fe-abd800cbfa44/0HC8132__55488H.jpg',
          modelCode: '0HC8132',
          sizeCode: '57',
          styleName: 'L109',
          upcCode: '725125358262',
        },
      ],
    };
    recap ? setRecap(recap) : mockRecap && setRecap(mockRecap);
  }, []);

  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    nextView: string
  ): void => {
    if (nextView) setView(nextView);
  };

  const onProductImageClick = (e: React.MouseEvent, upcCode: string): void => {
    e.preventDefault();

    const target = e.target as HTMLElement;

    if (
      typeof target.className === 'object' ||
      target.nextElementSibling instanceof SVGElement ||
      target.className.includes('Accordion')
    )
      return;

    setSelectedProduct(upcCode);
    setProductModalVisibility(true);
  };

  const processNameLabels: Partial<Record<string, string>> = {
    MNDL: t('receiving.confirmationFlow.deliveryManagement'),
    PDM: t('receiving.confirmationFlow.partialDeliveryManagement'),
    // other values
  };

  const processNameLabel = recap?.processName
    ? processNameLabels[recap.processName]
    : undefined;

  return (
    <StyledProductsWrapper>
      <StyledInfoWrapper>
        <StyledFirstRow>
          <Typography size={'lg'} font={'heavy'}>
            <DeliveryIcon />
            {recap?.deliveryNr}
          </Typography>
          <Typography size={'md'}>
            {recap?.status} <CheckCircleIcon htmlColor={colors.green} />
          </Typography>
        </StyledFirstRow>
        <StyledSecondRow>
          <Typography size={'md'} font={'light'}>
            {t('receiving.confirmationDate')}: {recap?.confirmationDate}
          </Typography>
          <Typography size={'md'} font={'light'}>
            {t('receiving.eventId')}: {recap?.eventId}
          </Typography>
          <Typography size={'md'} font={'light'}>
            {t('receiving.user')}: {recap?.webUser}
          </Typography>
        </StyledSecondRow>
      </StyledInfoWrapper>
      <UIBox
        width="100%;"
        justifyContent="space-between"
        margin="20px 0"
        padding="0 24px"
        flexDirection="column"
      >
        {processNameLabel && <UIWarning label={processNameLabel} />}
        <UIBox flexDirection="row">
          <ToggleButtonGroup
            orientation="horizontal"
            value={view}
            exclusive
            onChange={handleChange}
          >
            <ToggleButton value="list" aria-label="module">
              <ViewListIcon /> {t('receiving.list')}
            </ToggleButton>
            <ToggleButton value="grid" aria-label="list">
              <ViewModuleIcon /> {t('receiving.grid')}
            </ToggleButton>
          </ToggleButtonGroup>
        </UIBox>
      </UIBox>
      <>
        {view === 'grid' ? (
          <ProductList
            data={recap?.items || []}
            onProductImageClick={onProductImageClick}
            flowType={recap?.processName || 'RCC'}
          />
        ) : (
          <ProductTable
            type={Product.NOT_FOUND}
            data={recap?.items || []}
            flowType={recap?.processName || 'RCC'}
          />
        )}
      </>
      <ModalProductDetails
        open={openProductModal}
        onClose={(): void => setProductModalVisibility(false)}
        productCode={selectedProduct}
      />
      <CTAContainer
        type={'BACK'}
        onClick={(): void => history.push(AppRoutes.RECEIVING, { tab: 1 })}
      />
    </StyledProductsWrapper>
  );
});

PageReceivingRecap.displayName = 'PageReceivingRecap';
export default PageReceivingRecap;
